<template>
  <div class="affiliate-dashboard" :class="{ 'affiliate-dashboard--dark': isDarkSkin }">
    <breadcrumb-button>
      <template #items>
        <b-dropdown-item @click="openColumnsModal">
          <feather-icon icon="CheckSquareIcon" size="16"/>
          <span class="align-middle ml-50">{{ $t('columns.self') }}</span>
        </b-dropdown-item>
      </template>
    </breadcrumb-button>

    <div class="affiliate-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard v-model="filter" v-bind:fields="filterFields"
                   v-bind:hidden-fields="{'buTypes': viewKey!=='sales-dashboard' }"/>
      <calendar-bar v-model="dateRange" @input="refresh"/>
    </div>

    <b-row class="kpi-row">
      <b-col xl="3" md="6" sm="12" class="mb-2">
        <div class="kpi-card kpi-card--primary">
          <div class="kpi-card__icon">
            <feather-icon icon="UsersIcon" size="24"/>
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__title">{{ $t('Leads / FTDs') }}</div>
            <div class="kpi-card__value">{{ totals.leads }} <span class="kpi-card__value-sep">/</span> {{ totals.ftds }}</div>
            <div class="kpi-card__footer">
              <feather-icon icon="TrendingUpIcon" size="12" class="mr-25"/>
              {{ $t('Conversion') }}: {{ conversionRate }}%
            </div>
          </div>
          <div class="kpi-card__glow"></div>
        </div>
      </b-col>

      <b-col xl="3" md="6" sm="12" class="mb-2">
        <div class="kpi-card kpi-card--success">
          <div class="kpi-card__icon">
            <feather-icon icon="DollarSignIcon" size="24"/>
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__title">{{ $t('Net Deposits') }}</div>
            <div class="kpi-card__value">{{ convertToEURO(totals.netDeposits) }}</div>
            <div class="kpi-card__footer">
              <feather-icon icon="BriefcaseIcon" size="12" class="mr-25"/>
              {{ $t('Affiliates') }}: {{ totals.affiliates }}
            </div>
          </div>
          <div class="kpi-card__glow"></div>
        </div>
      </b-col>

      <b-col xl="3" md="6" sm="12" class="mb-2">
        <div class="kpi-card kpi-card--warning">
          <div class="kpi-card__icon">
            <feather-icon icon="StarIcon" size="24"/>
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__title">{{ $t('FTD Amount') }}</div>
            <div class="kpi-card__value">{{ convertToEURO(totals.ftdAmount) }}</div>
            <div class="kpi-card__footer">
              <feather-icon icon="RepeatIcon" size="12" class="mr-25"/>
              {{ $t('STD') }}: {{ convertToEURO(totals.stdAmount) }}
            </div>
          </div>
          <div class="kpi-card__glow"></div>
        </div>
      </b-col>

      <b-col xl="3" md="6" sm="12" class="mb-2">
        <div class="kpi-card kpi-card--danger">
          <div class="kpi-card__icon">
            <feather-icon icon="GlobeIcon" size="24"/>
          </div>
          <div class="kpi-card__body">
            <div class="kpi-card__title">{{ $t('Countries') }}</div>
            <div class="kpi-card__value">{{ totals.countries }}</div>
            <div class="kpi-card__footer">
              <feather-icon icon="UserCheckIcon" size="12" class="mr-25"/>
              {{ $t('Clients') }}: {{ clientIds.length }}
            </div>
          </div>
          <div class="kpi-card__glow"></div>
        </div>
      </b-col>
    </b-row>

    <div class="panel-card">
      <div class="panel-card__header">
        <div class="panel-card__title-wrap">
          <div class="panel-card__icon-chip">
            <feather-icon icon="TrendingUpIcon" size="18"/>
          </div>
          <h5 class="panel-card__title mb-0">{{ $t('menu.dashboard') }}</h5>
        </div>
      </div>
      <div class="panel-card__body">
        <affiliate-dashboard-table ref="affiliateDashboardTable" v-bind:filters="filter"
                                   v-bind:columns="columns" :has-selection="false" @calculate="setAffiliatesData"
                                   :lazy="true"/>
      </div>
    </div>

    <div class="panel-card mt-2">
      <div class="panel-card__header">
        <div class="panel-card__title-wrap">
          <div class="panel-card__icon-chip panel-card__icon-chip--alt">
            <feather-icon icon="UsersIcon" size="18"/>
          </div>
          <h5 class="panel-card__title mb-0">{{ $t('menu.clients') }}</h5>
        </div>
        <b-button size="sm" variant="flat-success" @click="createExcelFile" v-if="clientTable.hasData && hasPermission(permissions.EXPORT_EXCEL)">
          <i class="fa-regular fa-file-excel mr-50"></i>{{ $t('operations.export') }}
        </b-button>
      </div>
      <div class="panel-card__body" v-if="clientTable.hasData">
        <clients-table ref="clientTable" v-bind:filters="clientTable.filter" v-bind:columns="clientTable.columns" :has-selection="false"
                       :lazy="false" :default-pagination-size="10"/>
      </div>
      <div class="panel-card__empty" v-else>
        <feather-icon icon="InboxIcon" size="28" class="mb-50"/>
        <div>{{ $t('globalMessages.noDataFound') }}</div>
      </div>
    </div>

    <b-row class="mt-2">
      <b-col lg="6" sm="12" class="mb-2">
        <div class="panel-card">
          <div class="panel-card__header">
            <div class="panel-card__title-wrap">
              <div class="panel-card__icon-chip panel-card__icon-chip--alt">
                <feather-icon icon="PieChartIcon" size="18"/>
              </div>
              <h5 class="panel-card__title mb-0">{{ $t('Status Distribution') }}</h5>
            </div>
          </div>
          <div class="panel-card__body">
            <status-dashboard-table ref="statusDashboardTable" v-bind:filters="filter"
                                    v-bind:columns="columns" :has-selection="false"
                                    :lazy="true"
                                    :value="statusMapCount"/>
          </div>
        </div>
      </b-col>
    </b-row>

    <columns-modal ref="columns-modal" column-key="clientStatus" v-model="columns"
                   v-bind:columns-names="columnsNames"/>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import mixinBase from '@/components/mixin/mixinBase';
import { mapActions, mapGetters } from 'vuex';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import FilterCard from '@/components/widget/FilterCard';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import AffiliateDashboardFilterFields from '@/model/Dashboard/AffiliateDashboardFilterFields';
import AffiliateDashboardTable from '@/components/tables/AffiliateDashboardTable.vue';
import AffiliateDashboardSearchRequest from '@/model/Dashboard/AffiliateDashboardSearchRequest';
import AffiliateDashboard from '@/model/affiliate/AffiliateDashboard';
import ColumnsModal from '@/components/modal/columnsModal.vue';
import BreadcrumbButton from '@/components/widget/breadcrumbButton.vue';
import StatusDashboardTable from '@/components/tables/StatusDashboardTable.vue';
import ClientsTable from '@/components/tables/ClientsTable.vue';

export default {
  name: 'AffiliateDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],

  components: {
    ClientsTable,
    BreadcrumbButton,
    ColumnsModal,
    AffiliateDashboardTable,
    CalendarBar,
    FilterCard,
    StatusDashboardTable,
  },

  data: () => ({
    filter: AffiliateDashboardSearchRequest(),
    columnsNames: Object.keys(AffiliateDashboard()),
    columns: [],
    statusMapCount: {},
    filterFields: [],
    clientIds: [],
    dateRange: {
      from: '',
      until: '',
    },
    loading: false,
    totals: {
      leads: 0,
      ftds: 0,
      netDeposits: 0,
      ftdAmount: 0,
      stdAmount: 0,
      countries: 0,
      affiliates: 0,
    },
  }),
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    viewKey() {
      return this.$route.name || 'sales-dashboard';
    },
    conversionRate() {
      if (!this.totals.leads) return '0.00';
      return ((this.totals.ftds / this.totals.leads) * 100).toFixed(2);
    },
    clientTable() {
      let clientTableData = { hasData: false, filter: { ids: [], affiliateId: null }, columns: [] };

      if (this.clientIds == null || this.clientIds.length <= 0) {
        return clientTableData;
      }
      clientTableData.hasData = true;
      clientTableData.filter.ids = this.clientIds;
      if (this.filter.affiliateIds != null && this.filter.affiliateIds.length > 0) {
        clientTableData.filter.affiliateId = this.filter.affiliateIds;
      }
      clientTableData.columns = [
        { key: 'clientCell', enable: true },
        { key: 'fullName', enable: true },
        { key: 'affiliateName', enable: true },
        { key: 'creationTime', enable: true },
        { key: 'ftdTime', enable: true },
        { key: 'ftdOwnerId', enable: true },
      ];
      return clientTableData;
    }
  },
  created() {
    this.filterFields = AffiliateDashboardFilterFields();
    this.filter.range = {
      from: this.$moment().startOf('week').format('YYYY-MM-DD'),
      until: this.$moment().endOf('week').format('YYYY-MM-DD'),
    };
    this.refreshData();
  },
  methods: {
    ...mapActions('dashboard', ['getAffiliateDashboard']),
    ...mapActions('clients', ['refreshData']),
    setAffiliatesData(affiliates) {
      this.statusMapCount = affiliates.reduce((acc, affiliate) => {
        for (let [status, count] of Object.entries(affiliate.statuses || {})) {
          acc[status] = (acc[status] || 0) + count;
        }
        return acc;
      }, {});
      this.clientIds = affiliates
          .filter(a => a.clientIds != null && a.clientIds.length > 0)
          .flatMap(a => a.clientIds);

      const countrySet = new Set();
      let leads = 0, ftds = 0, netDeposits = 0, ftdAmount = 0, stdAmount = 0;
      affiliates.forEach(a => {
        leads += a.leads || 0;
        ftds += a.ftds || 0;
        netDeposits += a.netDeposits || 0;
        ftdAmount += a.ftdAmount || 0;
        stdAmount += a.stdAmount || 0;
        Object.keys(a.countries || {}).forEach(c => countrySet.add(c));
      });
      this.totals = {
        leads,
        ftds,
        netDeposits,
        ftdAmount,
        stdAmount,
        countries: countrySet.size,
        affiliates: affiliates.length,
      };
    },
    openColumnsModal() {
      this.$refs['columns-modal'].showModal();
    },
    refresh() {
      this.filter.range = {
        from: this.dateRange.from,
        until: this.dateRange.until,
      };
    },
    createExcelFile() {
      this.$refs['clientTable'].createExcelFile();
    },
  },
};
</script>

<style scoped lang="scss">
.affiliate-dashboard {
  position: relative;

  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

.kpi-row {
  margin-top: 0.5rem;
}

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28),
              0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  min-height: 118px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35),
                0 4px 8px rgba(20, 24, 50, 0.08);
  }

  &__icon {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
  }

  &__body {
    position: relative;
    z-index: 2;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.82rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.86;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value {
    font-size: 1.55rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value-sep {
    opacity: 0.55;
    font-weight: 500;
    margin: 0 0.15rem;
  }

  &__footer {
    margin-top: 0.45rem;
    font-size: 0.76rem;
    opacity: 0.9;
    display: flex;
    align-items: center;
  }

  &__glow {
    position: absolute;
    top: -40%;
    right: -20%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }

  &--primary {
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
  }

  &--success {
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%);
  }

  &--warning {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%);
  }

  &--danger {
    background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%);
  }
}

.panel-card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 10px 24px -16px rgba(20, 24, 50, 0.22),
              0 1px 3px rgba(20, 24, 50, 0.04);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px -18px rgba(20, 24, 50, 0.28),
                0 2px 4px rgba(20, 24, 50, 0.05);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid rgba(110, 117, 159, 0.12);
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  &__icon-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(0, 123, 255, 0.14), rgba(0, 123, 255, 0.04));
    color: #007BFF;

    &--alt {
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.14), rgba(16, 185, 129, 0.04));
      color: #10b981;
    }
  }

  &__title {
    font-size: 0.98rem;
    font-weight: 600;
    color: #1f2a44;
  }

  &__body {
    padding: 0.75rem 1rem 1rem;
    flex: 1;
  }

  &__empty {
    padding: 2.5rem 1rem;
    text-align: center;
    color: #8a91ad;
    font-size: 0.9rem;
  }
}

.affiliate-dashboard--dark {
  .panel-card {
    background: #283046;
    box-shadow: 0 10px 24px -16px rgba(0, 0, 0, 0.6);
  }

  .panel-card__header {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .panel-card__title {
    color: #d0d2d6;
  }

  .panel-card__empty {
    color: #a3aac2;
  }

  .panel-card__icon-chip {
    background: linear-gradient(135deg, rgba(77, 163, 255, 0.25), rgba(77, 163, 255, 0.05));
    color: #a8b6ff;

    &--alt {
      background: linear-gradient(135deg, rgba(46, 213, 162, 0.25), rgba(46, 213, 162, 0.05));
      color: #6bdcb1;
    }
  }
}

.dark-layout .apexcharts-title-text {
  background-color: rgba(255, 255, 255, 0);
  color: white !important;
}

@media (max-width: 576px) {
  .kpi-card {
    min-height: auto;
    padding: 1rem;
    &__value { font-size: 1.3rem; }
  }
}
</style>
